/**
 * Adapted from PlayCanvas Engine's gsplat-format.js at
 * d88289fb0c17be76ec4159974faf0d07d3582a6b (MIT License).
 */
import type {
  GSplatGraphicsAdapter,
  GSplatShaderLanguage,
  GSplatTextureFormat,
  GSplatTextureUsage,
} from '@/plugins/gsplat/graphics/GSplatGraphicsAdapter'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('gsplat:format')

export type GSplatStreamStorage = 'resource' | 'instance'

export interface GSplatStreamDescriptor {
  readonly name: string
  readonly format: GSplatTextureFormat
  readonly storage?: GSplatStreamStorage
}

export interface GSplatFormatOptions {
  readonly readGLSL: string
  readonly readWGSL: string
  readonly writeGLSL?: string
  readonly writeWGSL?: string
  readonly allowStreamRemoval?: boolean
  readonly dataFormat?: string | null
}

export interface GSplatComputeTextureBinding {
  readonly name: string
  readonly sampleType: 'float' | 'sint' | 'uint'
  readonly filterable: false
}

interface ShaderTextureType {
  readonly glslSampler: 'sampler2D' | 'isampler2D' | 'usampler2D'
  readonly glslReturn: 'vec4' | 'ivec4' | 'uvec4'
  readonly wgslTexture:
    | 'texture_2d<f32>'
    | 'texture_2d<i32>'
    | 'texture_2d<u32>'
    | 'texture_2d<uff>'
  readonly wgslReturn: 'vec4f' | 'vec4i' | 'vec4u'
  readonly sampleType: 'float' | 'sint' | 'uint'
}

const STREAM_NAME_PATTERN = /^[A-Za-z_][A-Za-z0-9_]*$/
const STREAM_USAGES = Object.freeze([
  'sampled',
  'copy-src',
  'copy-dst',
] as const satisfies readonly GSplatTextureUsage[])
const WORK_BUFFER_STREAM_USAGES = Object.freeze([
  ...STREAM_USAGES,
  'render-attachment',
] as const satisfies readonly GSplatTextureUsage[])

const DEFAULT_READ_GLSL = `
vec3 getCenter() { return loadDataCenter().xyz; }
vec4 getColor() { return loadDataColor(); }
vec3 getScale() { return loadDataScale().xyz; }
vec4 getRotation() { return loadDataRotation(); }
`

const DEFAULT_READ_WGSL = `
fn getCenter() -> vec3f { return loadDataCenter().xyz; }
fn getColor() -> vec4f { return loadDataColor(); }
fn getScale() -> vec3f { return loadDataScale().xyz; }
fn getRotation() -> vec4f { return loadDataRotation(); }
`

const SIMPLE_READ_GLSL = `
vec3 getCenter() { return loadDataCenter().xyz; }
vec4 getColor() { return loadDataColor(); }
vec3 getScale() { return vec3(loadDataCenter().w); }
vec4 getRotation() { return vec4(0.0, 0.0, 0.0, 1.0); }
`

const SIMPLE_READ_WGSL = `
fn getCenter() -> vec3f { return loadDataCenter().xyz; }
fn getColor() -> vec4f { return loadDataColor(); }
fn getScale() -> vec3f { return vec3f(loadDataCenter().w); }
fn getRotation() -> vec4f { return vec4f(0.0, 0.0, 0.0, 1.0); }
`

function canonicalField(value: string | number | null | undefined): string {
  const text = value == null ? '' : String(value)
  return `${text.length}:${text}`
}

function canonicalDescriptor(descriptor: GSplatStreamDescriptor): string {
  return [descriptor.name, descriptor.format, descriptor.storage ?? 'resource']
    .map(canonicalField)
    .join('|')
}

function fnv1a(value: string): number {
  let hash = 0x811c9dc5
  for (let index = 0; index < value.length; index++) {
    hash ^= value.charCodeAt(index)
    hash = Math.imul(hash, 0x01000193)
  }
  return hash >>> 0
}

function freezeDescriptor(descriptor: GSplatStreamDescriptor): GSplatStreamDescriptor {
  return Object.freeze({
    name: descriptor.name,
    format: descriptor.format,
    storage: descriptor.storage ?? 'resource',
  })
}

function shaderTextureType(format: GSplatTextureFormat): ShaderTextureType {
  if (
    format === 'r16uint' ||
    format === 'r32uint' ||
    format === 'rg32uint' ||
    format === 'rgba16uint' ||
    format === 'rgba32uint'
  ) {
    return {
      glslSampler: 'usampler2D',
      glslReturn: 'uvec4',
      wgslTexture: 'texture_2d<u32>',
      wgslReturn: 'vec4u',
      sampleType: 'uint',
    }
  }
  return {
    glslSampler: 'sampler2D',
    glslReturn: 'vec4',
    wgslTexture: format === 'rgba32float' ? 'texture_2d<uff>' : 'texture_2d<f32>',
    wgslReturn: 'vec4f',
    sampleType: 'float',
  }
}

function pascal(name: string): string {
  return name.charAt(0).toUpperCase() + name.slice(1)
}

/** PlayCanvas 对齐的共享 GSplat stream format，不按 backend 分叉对象模型。 */
export class GSplatFormat {
  readonly streams: readonly GSplatStreamDescriptor[]
  readonly allowStreamRemoval: boolean
  readonly dataFormat: string | null

  private readonly adapter: GSplatGraphicsAdapter
  private readonly readSources: Readonly<Record<GSplatShaderLanguage, string>>
  private writeSources: Readonly<Record<GSplatShaderLanguage, string>>
  private readonly streamNames: Set<string>
  private extraStreamList: readonly GSplatStreamDescriptor[] = Object.freeze([])
  private version = 0
  private cachedHash: number | undefined
  private cachedResourceStreams: readonly GSplatStreamDescriptor[] | null = null
  private cachedInstanceStreams: readonly GSplatStreamDescriptor[] | null = null

  constructor(
    adapter: GSplatGraphicsAdapter,
    streams: readonly GSplatStreamDescriptor[],
    options: GSplatFormatOptions
  ) {
    this.adapter = adapter
    this.allowStreamRemoval = options.allowStreamRemoval ?? false
    this.streams = Object.freeze(this.validateDescriptors(streams))
    this.streamNames = new Set(this.streams.map((stream) => stream.name))
    this.dataFormat = options.dataFormat ?? null
    if (!options.readGLSL || !options.readWGSL) {
      throw new Error('GSplatFormat 同时要求非空 readGLSL 与 readWGSL')
    }
    this.readSources = Object.freeze({ glsl: options.readGLSL, wgsl: options.readWGSL })
    this.writeSources = Object.freeze({
      glsl: options.writeGLSL ?? '',
      wgsl: options.writeWGSL ?? '',
    })
  }

  get graphicsAdapter(): GSplatGraphicsAdapter {
    return this.adapter
  }

  get hash(): number {
    if (this.cachedHash === undefined) {
      const fields = [
        canonicalField('base'),
        canonicalField(this.dataFormat),
        ...this.streams.map(canonicalDescriptor),
        canonicalField('extra'),
        ...this.extraStreamList.map(canonicalDescriptor),
        canonicalField(this.readSources.glsl),
        canonicalField(this.readSources.wgsl),
        canonicalField(this.writeSources.glsl),
        canonicalField(this.writeSources.wgsl),
      ]
      this.cachedHash = fnv1a(fields.join(';'))
    }
    return this.cachedHash
  }

  get extraStreamsVersion(): number {
    return this.version
  }

  get extraStreams(): readonly GSplatStreamDescriptor[] {
    return this.extraStreamList
  }

  get resourceStreams(): readonly GSplatStreamDescriptor[] {
    if (!this.cachedResourceStreams) {
      this.cachedResourceStreams = Object.freeze(
        [...this.streams, ...this.extraStreamList].filter((stream) => stream.storage !== 'instance')
      )
    }
    return this.cachedResourceStreams
  }

  get instanceStreams(): readonly GSplatStreamDescriptor[] {
    if (!this.cachedInstanceStreams) {
      this.cachedInstanceStreams = Object.freeze(
        this.extraStreamList.filter((stream) => stream.storage === 'instance')
      )
    }
    return this.cachedInstanceStreams
  }

  getReadCode(language: GSplatShaderLanguage = this.adapter.language): string {
    return this.readSources[language]
  }

  getWriteCode(language: GSplatShaderLanguage = this.adapter.language): string {
    return this.writeSources[language]
  }

  setWriteCode(writeGLSL: string, writeWGSL: string): void {
    this.writeSources = Object.freeze({ glsl: writeGLSL, wgsl: writeWGSL })
    this.cachedHash = undefined
  }

  getInputDeclarations(
    streamNames?: readonly string[],
    language: GSplatShaderLanguage = this.adapter.language
  ): string {
    return this.selectStreams(streamNames)
      .map((stream) => {
        const type = shaderTextureType(stream.format)
        const functionName = pascal(stream.name)
        if (language === 'glsl') {
          return (
            `\nuniform highp ${type.glslSampler} ${stream.name};\n` +
            `${type.glslReturn} load${functionName}() { return texelFetch(${stream.name}, splat.uv, 0); }\n` +
            `${type.glslReturn} load${functionName}WithIndex(uint index) { ` +
            `return texelFetch(${stream.name}, ivec2(index % splatTextureSize, index / splatTextureSize), 0); }\n`
          )
        }
        return (
          `\nvar ${stream.name}: ${type.wgslTexture};\n` +
          `fn load${functionName}() -> ${type.wgslReturn} { return textureLoad(${stream.name}, splat.uv, 0); }\n` +
          `fn load${functionName}WithIndex(index: u32) -> ${type.wgslReturn} { ` +
          `return textureLoad(${stream.name}, vec2i(i32(index % uniform.splatTextureSize), ` +
          `i32(index / uniform.splatTextureSize)), 0); }\n`
        )
      })
      .join('\n')
  }

  getComputeInputDeclarations(startBinding: number, streamNames?: readonly string[]): string {
    if (!Number.isSafeInteger(startBinding) || startBinding < 0) {
      throw new Error(`非法 GSplat compute startBinding: ${startBinding}`)
    }
    return this.selectStreams(streamNames)
      .map((stream, index) => {
        const type = shaderTextureType(stream.format)
        const functionName = pascal(stream.name)
        return (
          `\n@group(0) @binding(${startBinding + index}) var ${stream.name}: ${type.wgslTexture};\n` +
          `fn load${functionName}() -> ${type.wgslReturn} { return textureLoad(${stream.name}, splat.uv, 0); }\n` +
          `fn load${functionName}WithIndex(index: u32) -> ${type.wgslReturn} { ` +
          `return textureLoad(${stream.name}, vec2i(i32(index % uniforms.splatTextureSize), ` +
          `i32(index / uniforms.splatTextureSize)), 0); }\n`
        )
      })
      .join('\n')
  }

  getComputeBindFormats(streamNames?: readonly string[]): readonly GSplatComputeTextureBinding[] {
    return Object.freeze(
      this.selectStreams(streamNames).map((stream) =>
        Object.freeze({
          name: stream.name,
          sampleType: shaderTextureType(stream.format).sampleType,
          filterable: false as const,
        })
      )
    )
  }

  getOutputDeclarations(
    outputStreams: readonly GSplatStreamDescriptor[],
    language: GSplatShaderLanguage = this.adapter.language
  ): string {
    return outputStreams
      .map((stream, index) => this.outputDeclaration(stream, language, true, index))
      .join('\n')
  }

  getOutputStubs(
    streams: readonly GSplatStreamDescriptor[],
    language: GSplatShaderLanguage = this.adapter.language
  ): string {
    return streams.map((stream) => this.outputDeclaration(stream, language, false, 0)).join('\n')
  }

  getStream(name: string): GSplatStreamDescriptor | undefined {
    return [...this.streams, ...this.extraStreamList].find((stream) => stream.name === name)
  }

  addExtraStreams(streams: readonly GSplatStreamDescriptor[] | null | undefined): void {
    if (!streams || streams.length === 0) return

    const staged: GSplatStreamDescriptor[] = []
    const stagedNames = new Set<string>()
    for (const stream of streams) {
      if (this.streamNames.has(stream.name) || stagedNames.has(stream.name)) {
        log.warn(`GSplatFormat: stream "${stream.name}" 已存在，忽略重复项`)
        continue
      }
      const [normalized] = this.validateDescriptors([stream])
      staged.push(normalized)
      stagedNames.add(normalized.name)
    }
    if (staged.length === 0) return

    this.extraStreamList = Object.freeze([...this.extraStreamList, ...staged])
    for (const stream of staged) this.streamNames.add(stream.name)
    this.version++
    this.invalidateCaches()
  }

  removeExtraStreams(names: readonly string[]): void {
    if (!this.allowStreamRemoval) {
      throw new Error('GSplatFormat.removeExtraStreams 仅允许 WorkBuffer format 调用')
    }
    const remove = new Set(names)
    const next = this.extraStreamList.filter((stream) => !remove.has(stream.name))
    if (next.length === this.extraStreamList.length) return

    for (const stream of this.extraStreamList) {
      if (remove.has(stream.name)) this.streamNames.delete(stream.name)
    }
    this.extraStreamList = Object.freeze(next)
    this.version++
    this.invalidateCaches()
  }

  private validateDescriptors(
    descriptors: readonly GSplatStreamDescriptor[]
  ): GSplatStreamDescriptor[] {
    const names = new Set<string>()
    const normalized: GSplatStreamDescriptor[] = []
    for (const descriptor of descriptors) {
      if (!STREAM_NAME_PATTERN.test(descriptor.name)) {
        throw new Error(`非法 GSplat stream name: "${descriptor.name}"`)
      }
      if (names.has(descriptor.name)) {
        throw new Error(`GSplat stream name 重复: "${descriptor.name}"`)
      }
      names.add(descriptor.name)
      const stream = freezeDescriptor(descriptor)
      this.adapter.validateTextureDescriptor({
        label: `gsplat-format-preflight:${stream.name}`,
        width: 1,
        height: 1,
        format: stream.format,
        usages: this.allowStreamRemoval ? WORK_BUFFER_STREAM_USAGES : STREAM_USAGES,
      })
      normalized.push(stream)
    }
    return normalized
  }

  private invalidateCaches(): void {
    this.cachedHash = undefined
    this.cachedResourceStreams = null
    this.cachedInstanceStreams = null
  }

  private selectStreams(streamNames?: readonly string[]): readonly GSplatStreamDescriptor[] {
    const streams = [...this.streams, ...this.extraStreamList]
    if (!streamNames) return streams
    const selected = new Set(streamNames)
    return streams.filter((stream) => selected.has(stream.name))
  }

  private outputDeclaration(
    stream: GSplatStreamDescriptor,
    language: GSplatShaderLanguage,
    enabled: boolean,
    index: number
  ): string {
    const type = shaderTextureType(stream.format)
    const functionName = pascal(stream.name)
    if (language === 'glsl') {
      return (
        `\nvoid write${functionName}(${type.glslReturn} value) {\n` +
        `#if ${enabled ? 1 : 0}\n    pcFragColor${index} = value;\n#endif\n}\n`
      )
    }
    const colorSlot = index === 0 ? 'color' : `color${index}`
    return (
      `\nfn write${functionName}(value: ${type.wgslReturn}) {\n` +
      `#if ${enabled ? 1 : 0}\n    processOutput.${colorSlot} = value;\n#endif\n}\n`
    )
  }

  static createDefaultFormat(adapter: GSplatGraphicsAdapter): GSplatFormat {
    return new GSplatFormat(
      adapter,
      [
        { name: 'dataColor', format: 'rgba16float' },
        { name: 'dataCenter', format: 'rgba32float' },
        { name: 'dataScale', format: 'rgba16float' },
        { name: 'dataRotation', format: 'rgba16float' },
      ],
      { readGLSL: DEFAULT_READ_GLSL, readWGSL: DEFAULT_READ_WGSL }
    )
  }

  static createSimpleFormat(adapter: GSplatGraphicsAdapter): GSplatFormat {
    return new GSplatFormat(
      adapter,
      [
        { name: 'dataCenter', format: 'rgba32float' },
        { name: 'dataColor', format: 'rgba16float' },
      ],
      { readGLSL: SIMPLE_READ_GLSL, readWGSL: SIMPLE_READ_WGSL }
    )
  }
}
